// Web-exclusive Step: Profession Selection for Job Posting
// Single 3-level nested list: Profession Type -> Specialization -> Profession.
// Selecting a profession (level 3) sets the job's target profession directly,
// replacing the old two-step Type -> Specialization flow.

import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ActivityIndicator } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Search, Check, ChevronRight } from 'lucide-react';
import { webColors, webBorderRadius } from '../../../constants/webStyles';
import { getProfessionTypes, getProfessions } from '../../../services/shared';
import { getLocalizedProfessionName, getLocalizedProfessionTypeName, sortProfessionsGeneralFirst } from '../../../utils/professionUtils';
import { getParentTypeCode, professionTypeIcons, sharedStyles } from '../components';

const StepJobProfession = ({ formData, updateFormData }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedProfessionTypes, setExpandedProfessionTypes] = useState([]);
  const [expandedSubtypes, setExpandedSubtypes] = useState([]);
  const { country } = useSelector((state) => state.user);

  // Fetch profession types (level 1 + 2 hierarchy) and professions (level 3)
  const { data: typesData, isLoading: typesLoading } = useQuery({
    queryKey: ['professionTypes'],
    queryFn: () => getProfessionTypes(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const { data: professionsData, isLoading: professionsLoading } = useQuery({
    queryKey: ['professions', country],
    queryFn: () => getProfessions(country),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const isLoading = typesLoading || professionsLoading;

  const getTypeCode = (profession) =>
    profession.profession_type_code || profession.profession_type?.code || '';

  // Organize into a 3-level hierarchy: Type -> Sub-type -> Profession.
  // Empty branches (no professions) are pruned so only selectable paths show.
  const organizedProfessions = useMemo(() => {
    const professionTypes = typesData?.data?.data || [];
    const professions = professionsData?.data?.data || [];

    const hierarchy = {};

    professionTypes.forEach(type => {
      const code = type.code;
      const isTopLevel = !code.includes('-') || /^[a-z]-x$/i.test(code);
      if (isTopLevel) {
        hierarchy[code] = { ...type, subtypes: [], professions: [] };
      }
    });

    professionTypes.forEach(type => {
      const code = type.code;
      const isTopLevel = !code.includes('-') || /^[a-z]-x$/i.test(code);
      if (!isTopLevel) {
        const parentCode = getParentTypeCode(code);
        if (hierarchy[parentCode]) {
          hierarchy[parentCode].subtypes.push({ ...type, professions: [] });
        }
      }
    });

    professions.forEach(profession => {
      const typeCode = getTypeCode(profession);
      if (!typeCode || typeof typeCode !== 'string') return;

      const parentCode = getParentTypeCode(typeCode);
      if (!hierarchy[parentCode]) return;

      const subType = hierarchy[parentCode].subtypes.find(s => s.code === typeCode);
      if (subType) {
        subType.professions.push(profession);
      } else {
        hierarchy[parentCode].professions.push(profession);
      }
    });

    // Alphabetical, then each list's "general" entry pinned to the top
    Object.values(hierarchy).forEach(type => {
      type.subtypes.sort((a, b) =>
        getLocalizedProfessionTypeName(a, country).localeCompare(getLocalizedProfessionTypeName(b, country))
      );
      type.subtypes.forEach(sub => {
        sub.professions.sort((a, b) =>
          getLocalizedProfessionName(a, country).localeCompare(getLocalizedProfessionName(b, country))
        );
        sub.professions = sortProfessionsGeneralFirst(sub.professions, sub.code, country);
      });
      type.professions.sort((a, b) =>
        getLocalizedProfessionName(a, country).localeCompare(getLocalizedProfessionName(b, country))
      );
      type.professions = sortProfessionsGeneralFirst(type.professions, type.code, country);
    });

    const pruned = {};
    Object.entries(hierarchy).forEach(([code, type]) => {
      const subtypesWithProfs = type.subtypes.filter(s => s.professions.length > 0);
      if (subtypesWithProfs.length > 0 || type.professions.length > 0) {
        pruned[code] = { ...type, subtypes: subtypesWithProfs };
      }
    });

    return pruned;
  }, [typesData, professionsData, country]);

  // Search across all 3 levels (type, sub-type, profession names)
  const filteredProfessions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return organizedProfessions;

    const filtered = {};

    Object.entries(organizedProfessions).forEach(([code, type]) => {
      const typeMatches = getLocalizedProfessionTypeName(type, country).toLowerCase().includes(query);
      if (typeMatches) {
        filtered[code] = { ...type };
        return;
      }

      const matchingSubtypes = [];
      type.subtypes.forEach(subType => {
        const subTypeMatches = getLocalizedProfessionTypeName(subType, country).toLowerCase().includes(query);
        if (subTypeMatches) {
          matchingSubtypes.push({ ...subType });
        } else {
          const matchingProfs = subType.professions.filter(p =>
            getLocalizedProfessionName(p, country).toLowerCase().includes(query)
          );
          if (matchingProfs.length > 0) {
            matchingSubtypes.push({ ...subType, professions: matchingProfs });
          }
        }
      });

      const matchingDirectProfessions = type.professions.filter(p =>
        getLocalizedProfessionName(p, country).toLowerCase().includes(query)
      );

      if (matchingSubtypes.length > 0 || matchingDirectProfessions.length > 0) {
        filtered[code] = { ...type, subtypes: matchingSubtypes, professions: matchingDirectProfessions };
      }
    });

    return filtered;
  }, [organizedProfessions, searchQuery, country]);

  const searchActive = searchQuery.trim().length > 0;
  const selectedId = formData.profession?.id;

  const toggleProfessionType = (typeCode) => {
    setExpandedProfessionTypes(prev =>
      prev.includes(typeCode) ? prev.filter(c => c !== typeCode) : [...prev, typeCode]
    );
  };

  const toggleSubtype = (subtypeCode) => {
    setExpandedSubtypes(prev =>
      prev.includes(subtypeCode) ? prev.filter(c => c !== subtypeCode) : [...prev, subtypeCode]
    );
  };

  // Single-select: choosing a profession sets the job's target profession.
  const handleSelectProfession = (profession, groupName) => {
    updateFormData('profession', profession);
    updateFormData('specialization', getLocalizedProfessionName(profession, country));
    const parentCode = getParentTypeCode(getTypeCode(profession));
    if (parentCode) updateFormData('professionType', parentCode);
    if (groupName) updateFormData('professionSub', groupName);
  };

  const renderProfessionLeaf = (profession, groupName) => {
    const isSelected = selectedId === profession.id;
    const professionName = getLocalizedProfessionName(profession, country);
    if (!professionName || professionName.length === 0 || professionName === '#N/A') return null;

    return (
      <TouchableOpacity
        key={profession.id}
        style={[sharedStyles.professionItem, isSelected && sharedStyles.professionItemSelected]}
        onPress={() => handleSelectProfession(profession, groupName)}
        activeOpacity={0.7}
      >
        <View style={[sharedStyles.professionCheckbox, isSelected && sharedStyles.professionCheckboxSelected]}>
          {isSelected && <Check size={12} color="#ffffff" />}
        </View>
        <Text style={[sharedStyles.professionItemText, isSelected && sharedStyles.professionItemTextSelected]}>
          {professionName}
        </Text>
      </TouchableOpacity>
    );
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={webColors.primary} />
        <Text style={styles.loadingText}>Loading professions...</Text>
      </View>
    );
  }

  const typeEntries = Object.entries(filteredProfessions);

  return (
    <View style={styles.container}>
      <View style={styles.searchWrapper}>
        <View style={styles.searchIcon}>
          <Search size={16} color={webColors.textMuted} />
        </View>
        <TextInput
          style={styles.searchInput}
          placeholder="Search profession, specialization or type"
          placeholderTextColor={webColors.textMuted}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
      </View>

      <View style={styles.treeCard}>
        {typeEntries.length === 0 ? (
          <Text style={styles.noResultsText}>No professions found</Text>
        ) : (
          typeEntries.map(([typeCode, typeData]) => {
            const isExpanded = expandedProfessionTypes.includes(typeCode) || searchActive;
            const iconConfig = professionTypeIcons[typeCode] || professionTypeIcons.default;
            const hasSubtypes = typeData.subtypes && typeData.subtypes.length > 0;
            const typeLabel = getLocalizedProfessionTypeName(typeData, country);
            const allProfessionsInType = [
              ...typeData.professions,
              ...(typeData.subtypes || []).flatMap(s => s.professions),
            ];
            const typeHasSelection = allProfessionsInType.some(p => p.id === selectedId);

            return (
              <View key={typeCode} style={sharedStyles.professionTypeGroup}>
                {/* Level 1: Profession Type */}
                <TouchableOpacity
                  style={[sharedStyles.professionTypeHeader, isExpanded && sharedStyles.professionTypeHeaderExpanded]}
                  onPress={() => toggleProfessionType(typeCode)}
                  activeOpacity={0.7}
                >
                  <Text style={sharedStyles.professionTypeIcon}>{iconConfig.icon}</Text>
                  <Text style={[sharedStyles.professionTypeLabel, isExpanded && sharedStyles.professionTypeLabelExpanded]}>
                    {typeLabel}
                  </Text>
                  {typeHasSelection && (
                    <View style={[sharedStyles.professionTypeCount, { backgroundColor: iconConfig.bgColor }]}>
                      <Check size={11} color={iconConfig.color} />
                    </View>
                  )}
                  <ChevronRight
                    size={16}
                    color={isExpanded ? webColors.primary : webColors.textMuted}
                    style={{ transform: [{ rotate: isExpanded ? '90deg' : '0deg' }] }}
                  />
                </TouchableOpacity>

                {isExpanded && (
                  <View style={sharedStyles.professionItemsList}>
                    {/* Level 2: Sub-types */}
                    {hasSubtypes && typeData.subtypes.map(subType => {
                      const isSubExpanded = expandedSubtypes.includes(subType.code) || searchActive;
                      const subLabel = getLocalizedProfessionTypeName(subType, country);
                      const subHasSelection = subType.professions.some(p => p.id === selectedId);

                      return (
                        <View key={subType.code} style={sharedStyles.professionSubtypeGroup}>
                          <TouchableOpacity
                            style={[sharedStyles.professionSubtypeHeader, isSubExpanded && sharedStyles.professionSubtypeHeaderExpanded]}
                            onPress={() => toggleSubtype(subType.code)}
                            activeOpacity={0.7}
                          >
                            <Text style={[sharedStyles.professionSubtypeLabel, isSubExpanded && sharedStyles.professionSubtypeLabelExpanded]}>
                              {subLabel}
                            </Text>
                            {subHasSelection && (
                              <View style={[sharedStyles.professionSubtypeCount, { backgroundColor: iconConfig.bgColor }]}>
                                <Check size={10} color={iconConfig.color} />
                              </View>
                            )}
                            <ChevronRight
                              size={14}
                              color={isSubExpanded ? webColors.primary : webColors.textMuted}
                              style={{ transform: [{ rotate: isSubExpanded ? '90deg' : '0deg' }] }}
                            />
                          </TouchableOpacity>

                          {/* Level 3: Professions under sub-type */}
                          {isSubExpanded && (
                            <View style={sharedStyles.professionSubtypeItemsList}>
                              {subType.professions.map(p => renderProfessionLeaf(p, subLabel))}
                            </View>
                          )}
                        </View>
                      );
                    })}

                    {/* Level 3: Direct professions (types without sub-types) */}
                    {typeData.professions.map(p => renderProfessionLeaf(p, typeLabel))}
                  </View>
                )}
              </View>
            );
          })
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
    color: webColors.textSecondary,
  },
  searchWrapper: {
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: [{ translateY: -8 }],
    zIndex: 1,
  },
  searchInput: {
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingHorizontal: 46,
    paddingVertical: 14,
    fontSize: 15,
    color: webColors.textPrimary,
  },
  treeCard: {
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    backgroundColor: webColors.cardBackground,
    overflow: 'hidden',
    paddingVertical: 4,
  },
  noResultsText: {
    fontSize: 14,
    color: webColors.textMuted,
    textAlign: 'center',
    paddingVertical: 32,
  },
});

export default StepJobProfession;
